import React, { useEffect, useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { get, post } from './api';
import { t, LanguageSwitcher } from '@omnifuse/shared/frontend/i18n';
import { SettingsPanel } from '@omnifuse/shared/frontend/SettingsPanel';
import { get as apiGet, post as apiPost, put as apiPut } from './api';

const S = {
  page: { maxWidth: 1100, margin: '0 auto', padding: 24 } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: 16 } as React.CSSProperties,
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 } as React.CSSProperties,
  btn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  btnPrimary: { padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 10px', borderBottom: '2px solid #eee', color: '#555' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0' },
  tab: (a: boolean) => ({ padding: '8px 18px', cursor: 'pointer', borderBottom: a ? '2px solid #1a73e8' : '2px solid transparent', color: a ? '#1a73e8' : '#555', fontWeight: a ? 600 : 400, fontSize: 14 }) as React.CSSProperties,
};

function PropensityView() {
  const [rows, setRows] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    get('/api/v1/propensity').then((r: any) => setRows(r.data || [])).catch(() => {});
    get('/api/v1/stats').then((r: any) => setStats(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const compute = async () => {
    setBusy(true);
    try { const r: any = await post('/api/v1/propensity/compute'); alert(`分析 ${r.data.orders_analyzed} 订单，评分 ${r.data.customers_scored} 位客户`); load(); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  const genOffers = async () => {
    setBusy(true);
    try { const r: any = await post('/api/v1/offers/generate', {}); alert(`高倾向客户 ${r.data.candidates} 位，生成 ${r.data.offers_created} 个定向优惠包`); load(); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  return (
    <>
      {stats && (
        <div style={S.grid}>
          <div style={S.card}><div style={{ fontSize: 12, color: '#888' }}>{t('优惠包总数')}</div><div style={{ fontSize: 24, fontWeight: 700 }}>{stats.offers_total}</div></div>
          <div style={S.card}><div style={{ fontSize: 12, color: '#888' }}>{t('已触达')}</div><div style={{ fontSize: 24, fontWeight: 700 }}>{stats.offers_sent}</div></div>
          <div style={S.card}><div style={{ fontSize: 12, color: '#888' }}>{t('已转化订阅')}</div><div style={{ fontSize: 24, fontWeight: 700, color: '#188038' }}>{stats.converted}</div></div>
          <div style={S.card}><div style={{ fontSize: 12, color: '#888' }}>{t('转化率')}</div><div style={{ fontSize: 24, fontWeight: 700 }}>{stats.conversion_rate}%</div></div>
        </div>
      )}
      <div style={{ ...S.card, display: 'flex', gap: 10 }}>
        <button style={S.btnPrimary} disabled={busy} onClick={compute}>{t('🔮 计算订阅倾向')}</button>
        <button style={{ ...S.btnPrimary, background: '#188038' }} disabled={busy} onClick={genOffers}>{t('🎁 生成定向优惠包')}</button>
      </div>
      <div style={S.card}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('客户')}</th><th style={S.th}>{t('倾向分')}</th><th style={S.th}>{t('核心商品')}</th><th style={S.th}>{t('复购次数')}</th><th style={S.th}>{t('周期')}</th><th style={S.th}>{t('评分因素')}</th></tr></thead>
          <tbody>
            {rows.map((r: any) => (
              <tr key={r.customer_id}>
                <td style={S.td}>{r.customer_email || r.customer_id}</td>
                <td style={{ ...S.td, fontWeight: 700, color: r.score >= 50 ? '#188038' : '#888' }}>{Math.round(r.score)}</td>
                <td style={S.td}>{r.top_product_title}</td>
                <td style={S.td}>{r.purchase_count}</td>
                <td style={S.td}>{r.avg_interval_days ? `${Math.round(r.avg_interval_days)}天` : '-'}</td>
                <td style={{ ...S.td, fontSize: 12 }}>{JSON.parse(r.factors_json || '[]').join('；')}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td style={S.td} colSpan={6}>{t('暂无评分 — 点击"计算订阅倾向"')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

function OffersView() {
  const [rows, setRows] = useState<any[]>([]);

  const load = () => get('/api/v1/offers').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id: string, status: string) => {
    try { await post(`/api/v1/offers/${id}/status`, { status }); load(); } catch (e: any) { alert(e.message); }
  };

  const sync = async (id: string) => {
    try { const r: any = await post(`/api/v1/offers/${id}/sync`, { platform: 'shopify_native' }); alert(`已生成 selling plan payload:\n${JSON.stringify(r.data.payload.selling_plan, null, 2)}`); }
    catch (e: any) { alert(e.message); }
  };

  return (
    <div style={S.card}>
      <table style={S.table}>
        <thead><tr>
          <th style={S.th}>{t('客户')}</th><th style={S.th}>{t('商品')}</th><th style={S.th}>{t('频率')}</th><th style={S.th}>{t('折扣')}</th>
          <th style={S.th}>{t('年省')}</th><th style={S.th}>{t('话术')}</th><th style={S.th}>{t('状态')}</th><th style={S.th}>{t('操作')}</th>
        </tr></thead>
        <tbody>
          {rows.map((r: any) => (
            <tr key={r.id}>
              <td style={S.td}>{r.customer_email || r.customer_id}</td>
              <td style={S.td}>{r.product_title}</td>
              <td style={S.td}>{r.frequency_days}天</td>
              <td style={S.td}>{r.discount_pct}%</td>
              <td style={S.td}>${r.annual_savings}</td>
              <td style={{ ...S.td, fontSize: 12, maxWidth: 260 }}>{r.pitch_message}</td>
              <td style={S.td}>{r.status}</td>
              <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                <span style={{ display: 'inline-flex', gap: 6 }}>
                  {r.status === 'draft' && <button style={S.btnPrimary} onClick={() => setStatus(r.id, 'sent')}>{t('标记触达')}</button>}
                  {r.status === 'sent' && (
                    <>
                      <button style={S.btnPrimary} onClick={() => setStatus(r.id, 'converted')}>{t('已转化')}</button>
                      <button style={S.btn} onClick={() => setStatus(r.id, 'declined')}>{t('拒绝')}</button>
                    </>
                  )}
                  {r.status === 'converted' && <button style={S.btn} onClick={() => sync(r.id)}>{t('同步平台')}</button>}
                </span>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={S.td} colSpan={8}>{t('暂无优惠包')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function App() {
  const [tab, setTab] = useState('propensity');
  return (
    <div style={S.page}>
      <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 1000 }}><LanguageSwitcher /></div>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>SubscribeConvertAI</h1>
      <p style={{ color: '#666', marginBottom: 16 }}>{t('超个性化订阅转化 — 倾向预测 · 定向优惠包 · 平台同步')}</p>
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 20 }}>
        <div style={S.tab(tab === 'propensity')} onClick={() => setTab('propensity')}>{t('倾向评分')}</div>
        <div style={S.tab(tab === 'offers')} onClick={() => setTab('offers')}>{t('定向优惠包')}</div>
        <div style={S.tab(tab === 'settings')} onClick={() => setTab('settings')}>{t('设置')}</div>
      </div>
      {tab === 'propensity' && <PropensityView />}
      {tab === 'offers' && <OffersView />}
      {tab === 'settings' && <SettingsPanel api={{ get: apiGet, post: apiPost, put: apiPut }} />}
    </div>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(<App />);
